import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { DeviceDetectorService } from "ngx-device-detector";
import { merge } from "rxjs";
import { tap } from "rxjs/operators";
import { AbstractBiditComponent } from "../../shared/controls/abstract.bidit.component";
import { CustomerImport } from "../../shared/models/customer.import.interface";
import { CustomerType } from "../../shared/models/customer.type.interface";
import { User } from "../../shared/models/user.interface";
import { CustomerImportDataSource } from "../datasources/customer-import-data-source";
import { ImportDialog, ImportDialogData } from "./import-dialog/import.dialog";
import { CustomerFilter } from "../models/customer.filter";
import { CustomerService } from "../services/customer.service";
import { ImportEditData, ImportEditDialog } from "./import-edit/import-edit.dialog";
import { Office } from "../../shared/models/office.interface";
import { PaymentTerm } from "../../shared/models/payment.term.interface";
import { PaymentType } from "../../shared/models/payment.type.interface";
import { RolePermission } from "../../shared/models/role.permission.enum";

export interface CustomerImportSummary {
  customerCount: number;

  offices: Office[];
  customerTypes: CustomerType[];
  paymentTerms: PaymentTerm[];
  paymentTypes: PaymentType[];
  salespersons: User[];
}

@Component({
  selector: 'app-import',
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.scss']
})
export class ImportComponent extends AbstractBiditComponent implements AfterViewInit, OnInit, OnDestroy {
  dataSource: CustomerImportDataSource;
  displayedColumns: string[] =
    ['customerName', 'address', 'contact', 'summary', 'salesperson', 'action'];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  summary: CustomerImportSummary;

  filter: CustomerFilter = { salespersonId: '', customerType: null } as CustomerFilter;
  canAddCustomer: boolean = false;

  constructor(private dialog: MatDialog, protected customerService: CustomerService,
    private deviceDetectorService: DeviceDetectorService) {
    super(deviceDetectorService);
  }

  ngOnInit() {
    this.canAddCustomer = CustomerService.hasPermission(RolePermission.AddCustomer);

    this.dataSource = new CustomerImportDataSource(this.customerService);
    this.loadSummary();
  }

  loadSummary() {
    this.isRequesting = true;
    this.customerService.getImportSummary(this.filter)
      .subscribe({
        next: (data) => {
          this.summary = data;
          this.loadCustomers();
        },
        error: (error: string) => this.errors = error
      });
  }

  ngAfterViewInit(): void {
    // reset the paginator after sorting
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => this.loadCustomers())
      )
      .subscribe();

    this.paginator.page
      .pipe(
        tap(() => this.loadCustomers())
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.sort.sortChange.unsubscribe();
    this.paginator.page.unsubscribe();
  }

  loadCustomers() {
    this.isRequesting = false;
    this.dataSource.loadCustomers(this.filter, this.sort.active,
      this.sort.direction, this.paginator.pageIndex);
  }

  importFile() {
    let dialogData = {

    } as ImportDialogData;

    const dialogRef = this.dialog.open(ImportDialog, {
      width: '700px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadSummary();
      }
    });
  }

  invalidImport(customer: CustomerImport): boolean {
    return (customer.assignedTo == undefined ||
      customer.paymentTerm == undefined ||
      customer.paymentType == undefined ||
      customer.contact_FirstName == undefined ||
      customer.contact_LastName == undefined ||
      (customer.contact_Email == undefined && customer.contact_Phone == undefined && customer.contact_Mobile == undefined) ||
      customer.paymentType == undefined ||
      customer.office == undefined ||
      customer.customer_Address1 == undefined ||
      customer.customer_City == undefined ||
      customer.customer_State == undefined ||
      customer.customer_Postal == undefined);
  }

  editCustomerImport(customer: CustomerImport) {
    let dialogData = {
      customer: customer,

      offices: this.summary.offices,
      paymentTerms: this.summary.paymentTerms,
      paymentTypes: this.summary.paymentTypes,
      customerTypes: this.summary.customerTypes,
      salespersons: this.summary.salespersons
    } as ImportEditData;

    const dialogRef = this.dialog.open(ImportEditDialog, {
      width: '700px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.loadSummary();
      }
    });
  }

  deleteCustomerImport(customer: CustomerImport) {
    var proceed = confirm($localize`Discard this customer import record?`);
    if (!proceed) return;

    this.customerService.deleteCustomerImport(customer.customerImport_ID)
      .subscribe(resp => {
        this.loadSummary();
      });
  }

  cancelImport() {
    var proceed = confirm($localize`Discard this customer import file?`);
    if (!proceed) return;

    this.isRequesting = true;
    this.customerService.submitCustomerImport(false)
      .subscribe(resp => {
        this.loadSummary();
      });
  }

  applyImport() {
    var proceed = confirm($localize`Apply this customer import file?`);
    if (!proceed) return;

    this.isRequesting = true;
    this.customerService.submitCustomerImport(true)
      .subscribe({
        next: (resp) => {
          this.loadSummary();
          alert($localize`Customers imported to Bidit`);
        },
        error: (error: string) => {
          alert($localize`Unexpected errors importing customer records.  Please try again later.`);
        }
      });
  }
}
